import { Text } from "@/components/ui/baseUi/text"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import type { ScoreboardRankingEntity } from "@/modules/scores/scoreboard/entities/scoreboardRankingEntity"
import { getDisplayedScore } from "@/modules/scores/scoreUtils"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import { useUnit } from "@/modules/utils/hooks/useUnit"
import type { BoxSessionDetailsEntity } from "@/modules/boxSlotSession/entities/boxSessionEntity"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { useScoreboard } from "@modules/scores/scoreboard/hooks/useScoreboard"
import { ScoreDifficultyFilterEnum, type ExerciseScoreCategory } from "@modules/scores/scoreTypes"
import { LoadingSpinner } from "@modules/ui/components/loading"

type Props = {
  session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity | BoxSessionDetailsEntity
  selectedExercise: SessionBlockExerciseEntity
  selectedDifficulty: ScoreDifficultyFilterEnum
  categorySelected: ExerciseScoreCategory
  className?: string
} & React.ComponentProps<"div">

export function HeaderPodium({
  session,
  selectedExercise,
  selectedDifficulty,
  categorySelected,
  className,
  ...props
}: Props) {
  const { isLoading: scoreboardLoading, data: scoreboard } = useScoreboard(
    "programId" in session ? session.programId : "",
    session.id,
    selectedExercise?.id,
    selectedDifficulty,
    categorySelected,
  )

  if (scoreboardLoading || !(scoreboard && scoreboard.content.length > 2)) return null

  const podium = scoreboard.content.slice(0, 3)

  return (
    <>
      {podium ? (
        <div className={cn("mb-3.5 flex items-end justify-between px-20", className)} {...props}>
          <PlacementPodiumRound userScore={podium[1]!} size="size-22.5" />
          <PlacementPodiumRound userScore={podium[0]!} size="size-31.5" />
          <PlacementPodiumRound userScore={podium[2]!} size="size-22.5" />
        </div>
      ) : (
        <LoadingSpinner />
      )}
    </>
  )
}

type PlacementPodiumRoundProps = {
  userScore: ScoreboardRankingEntity
  size: string
  className?: string
} & React.ComponentProps<"div">

function PlacementPodiumRound({ userScore, size, className }: PlacementPodiumRoundProps) {
  const unit = useUnit()
  const language = useLanguage()

  return (
    <div className={cn("flex flex-col items-center", className)}>
      <div className="relative mb-4 flex flex-col justify-center">
        <ProfileAvatar
          className={cn("mr-2 mb-1 border-3 border-neutral-100", size)}
          profile={{ avatar: userScore.avatar ?? null, username: userScore.firstname || "" }}
        />
        <div className="absolute bottom-[-8px] left-1/2 flex h-6.5 -translate-x-1/2 transform items-center justify-center rounded-full border-2 border-neutral-100 bg-neutral-900 px-2 py-1">
          <Text fontStyle="LM" className="text-textOnColor-body">
            {userScore.position}
          </Text>
        </div>
      </div>
      {userScore.score && userScore.score.valueDecimal !== null && (
        <Text fontStyle="BS" className="text-textDefault-heading">
          {getDisplayedScore(
            userScore.score.valueDecimal,
            userScore.score.additionalValueDecimal,
            userScore.scoreType,
            userScore.score.timeCapExceeded ?? false,
            unit,
            language,
          )}
        </Text>
      )}
    </div>
  )
}
